import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import { categories, articles, faqItems } from '@/mocks/siteData';

const featuredArticles = articles.filter((a) => a.featured);

const stats = [
  { label: '收录真人网站', value: '240+' },
  { label: '内容分类', value: '6大类' },
  { label: '精选文章', value: '50+' },
  { label: '服务用户', value: '10万+' },
];

const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  'mainEntity': faqItems.map((item) => ({
    '@type': 'Question',
    'name': item.question,
    'acceptedAnswer': {
      '@type': 'Answer',
      'text': item.answer,
    },
  })),
};

export default function Home() {
  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': '真人网站精选推荐 — 真实内容平台导航',
        'description': '真人网站精选推荐平台，专注真人网站定义、分类与筛选标准，帮助用户快速找到优质真人网站资源。',
        'url': 'https://recosearch.github.io/',
      })}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          {/* Hero Section */}
          <section className="relative w-full h-[520px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20inviting%20digital%20platform%20concept%20with%20abstract%20geometric%20patterns%2C%20soft%20golden%20and%20cream%20color%20palette%2C%20clean%20modern%20minimalist%20design%2C%20representing%20trust%20and%20authenticity%20in%20online%20world%2C%20editorial%20style%20with%20subtle%20texture&width=1600&height=900&seq=hero-main-01&orientation=landscape"
              alt="真人网站精选推荐"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/40"></div>
            <div className="relative z-10 text-center px-4 w-full max-w-4xl mx-auto">
              <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl text-background-50 mb-4 leading-tight">
                真人网站精选推荐
              </h1>
              <p className="text-base md:text-lg text-background-200/90 max-w-2xl mx-auto mb-8 leading-relaxed">
                专注真人网站定义、分类与筛选标准，帮助用户快速找到优质真人网站资源，了解真人网站的安全访问方式、内容特点及使用注意事项，是发现可信真人网站的一站式参考指南。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category"
                  className="px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  浏览分类
                </Link>
                <Link
                  to="/about"
                  className="px-8 py-3 rounded-full border border-background-200/50 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          {/* Stats Section */}
          <section className="w-full bg-background-50">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 py-10 md:py-14">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="font-heading text-3xl md:text-4xl text-primary-600 mb-1">{stat.value}</div>
                    <div className="text-sm text-foreground-600">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Categories Section */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                  真人网站分类导航
                </h2>
                <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
                  按类别浏览真人网站资源，快速找到你感兴趣的内容类型
                </p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category?tab=${cat.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.02] cursor-pointer"
                  >
                    <div className="relative w-full h-48 overflow-hidden">
                      <img
                        src={cat.imageUrl}
                        alt={cat.name}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                      <div className="absolute bottom-3 left-4 right-4 flex items-center gap-2">
                        <div className="w-8 h-8 rounded-lg bg-background-50/90 flex items-center justify-center">
                          <i className={`${cat.icon} text-primary-600 text-sm`}></i>
                        </div>
                        <span className="text-background-50 font-medium text-sm">{cat.name}</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <p className="text-xs text-foreground-600 leading-relaxed line-clamp-2">{cat.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <span className="text-xs text-foreground-500">{cat.count} 个网站</span>
                        <span className="text-xs text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap">
                          查看详情 <i className="ri-arrow-right-line text-[10px]"></i>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>

          {/* Featured Articles */}
          <section className="w-full bg-background-50 py-14 md:py-20">
            <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="flex items-center justify-between mb-12">
                <div>
                  <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                    精选推荐文章
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600">
                    深入了解真人网站的方方面面，获取最实用的真人网站导航指南
                  </p>
                </div>
                <Link
                  to="/category?tab=all-articles"
                  className="hidden sm:flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {featuredArticles.map((article) => {
                  const cat = categories.find((c) => c.id === article.categoryId);
                  return (
                    <Link
                      key={article.id}
                      to={`/article/${article.slug}`}
                      className="group bg-background-100 rounded-xl overflow-hidden transition-all duration-300 hover:scale-[1.01] cursor-pointer flex flex-col sm:flex-row"
                    >
                      <div className="relative w-full sm:w-48 h-44 sm:h-auto flex-shrink-0 overflow-hidden">
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-4 md:p-5 flex flex-col justify-center flex-1">
                        {cat && (
                          <span className="inline-block px-2.5 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium mb-2 w-fit">
                            {cat.name}
                          </span>
                        )}
                        <h3 className="font-heading text-base md:text-lg text-foreground-900 mb-2 leading-snug group-hover:text-primary-600 transition-colors line-clamp-2">
                          {article.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-2 mb-3">
                          {article.excerpt}
                        </p>
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span>{article.date}</span>
                          <span>·</span>
                          <span>{article.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
              <div className="mt-8 text-center sm:hidden">
                <Link
                  to="/category?tab=all-articles"
                  className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  查看全部文章 <i className="ri-arrow-right-line text-xs"></i>
                </Link>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="w-full bg-background-100 py-14 md:py-20">
            <div className="max-w-3xl mx-auto px-4 md:px-6 lg:px-10">
              <div className="text-center mb-12">
                <h2 className="font-heading text-2xl md:text-4xl text-foreground-900 mb-3">
                  常见问题解答
                </h2>
                <p className="text-sm md:text-base text-foreground-600">
                  关于真人网站的常见疑问，这里都有答案
                </p>
              </div>
              <div className="space-y-3">
                {faqItems.map((faq, idx) => (
                  <details key={idx} className="group bg-background-50 rounded-xl overflow-hidden">
                    <summary className="flex items-center justify-between px-5 py-4 cursor-pointer list-none">
                      <h4 className="text-sm md:text-base font-medium text-foreground-900 pr-4">{faq.question}</h4>
                      <div className="w-6 h-6 flex-shrink-0 rounded-full bg-primary-100 flex items-center justify-center group-open:rotate-45 transition-transform duration-200">
                        <i className="ri-add-line text-primary-600 text-xs"></i>
                      </div>
                    </summary>
                    <div className="px-5 pb-5">
                      <p className="text-sm text-foreground-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </details>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="w-full relative py-16 md:py-24 overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20warm%20gradient%20background%20with%20soft%20overlapping%20circles%20and%20geometric%20shapes%2C%20golden%20and%20cream%20color%20palette%2C%20clean%20minimalist%20modern%20design%2C%20subtle%20texture%2C%20editorial%20quality&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
              alt=""
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-background-900/60 to-background-900/40"></div>
            <div className="relative z-10 text-center px-4 max-w-2xl mx-auto">
              <h2 className="font-heading text-2xl md:text-4xl text-background-50 mb-4">
                发现更多优质真人网站
              </h2>
              <p className="text-sm md:text-base text-background-200/90 mb-8 leading-relaxed">
                加入我们的真人网站精选推荐社区，获取最新的真人网站导航资讯与精选资源，开启真实可信的网络体验。
              </p>
              <Link
                to="/category"
                className="inline-flex items-center gap-2 px-8 py-3 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                立即探索 <i className="ri-arrow-right-line text-sm"></i>
              </Link>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}